import {useCallback, useEffect, useRef, useState} from 'react';

// Collapses a panel when the referenced element is scrolled down, expands on scroll up.
// A short lock after each toggle absorbs the layout shift so it can't re-fire in reverse.
export default function useCollapseOnScroll() {
    const [el, setEl] = useState<HTMLElement | null>(null);
    const [collapsed, setCollapsed] = useState(false);
    const collapsedRef = useRef(false);
    const lastRef = useRef(0);
    const lockedRef = useRef(false);
    const lockTimerRef = useRef<ReturnType<typeof setTimeout>>();

    const lock = useCallback(() => {
        lockedRef.current = true;
        clearTimeout(lockTimerRef.current);
        lockTimerRef.current = setTimeout(() => {
            lockedRef.current = false;
            if(el) lastRef.current = el.scrollTop;
        }, 380);
    }, [el]);

    useEffect(() => {
        if(!el) return;
        lastRef.current = el.scrollTop;
        const onScroll = () => {
            const st = el.scrollTop;
            if(lockedRef.current) {
                lastRef.current = st;
                return;
            }
            const dir = st - lastRef.current;
            if(Math.abs(dir) < 6) {
                lastRef.current = st;
                return;
            }
            if(dir > 0 && !collapsedRef.current) {
                collapsedRef.current = true;
                setCollapsed(true);
                lock();
            } else if(dir < 0 && collapsedRef.current) {
                collapsedRef.current = false;
                setCollapsed(false);
                lock();
            }
            lastRef.current = st;
        };
        el.addEventListener('scroll', onScroll, {passive: true});
        return () => {
            el.removeEventListener('scroll', onScroll);
            clearTimeout(lockTimerRef.current);
        };
    }, [el, lock]);

    const expand = useCallback(() => {
        collapsedRef.current = false;
        setCollapsed(false);
        lock();
    }, [lock]);

    return {collapsed, expand, ref: setEl};
}
